import _ from 'lodash';
import config from 'client/shared/config';
import autobind from 'autobind-decorator';

import {
	observable,
	action,
	reaction,
	computed,
	runInAction
} from 'mobx';


import BaseStore from './base-store';
import Song from './song';

import apiStore from './api-store';
import artistStore from './artist-store';
import playlistStore from './playlist-store';

const { routes } = config;

function removeBadChars (value) {
	return value
		.replace(/['|"|,|`]/g, '')
		.trim()
		.replace(/\s/g, '_');
}

class SongsStore extends BaseStore {
	apiRoute = config.getRoute('songs');

	constructor () {
		super();

		this.disposeReaction = reaction(
			() => apiStore.data.songs,
			this.onFetchSuccess
		);

		this.disposeSearchReaction = reaction(
			() => this.searchTerm,
			this.search
		);
	}

	destroy () {
		this.disposeReaction();
	}

	/**
	 * Observables & Computeds
	 */

 	@observable
 	isUpLoading = false;

	@observable
	_isPolling = false;

	@observable
	_showAdminOptions = false;

	@computed
	get showAdminOptions () {
		return this._showAdminOptions;
	}

	set showAdminOptions (showAdminOptions) {
		this._showAdminOptions = showAdminOptions;
	}

	@observable
	_currentUploadProgress = 0;

 	@computed
 	get currentUploadProgress () {
		return Math.ceil((apiStore._progressLoaded / apiStore._progressTotal) * 100);
	};
	set currentUploadProgress (value) {
		this.currentUploadProgress = isNaN(value) ? 0 : value;
	}

 	@observable
 	_activeSongRowID = null;

	@observable
	_currentSong = {};

	@computed
	get currentSong () {
		const currentSong = _.isString(this._currentSong) ?
			this.getSongByID(this._currentSong) : this._currentSong;

		if (_.isEmpty(currentSong)) {

			return null;
		}

		const artist = artistStore.getArtistByID(currentSong.artistID);

		if (_.isEmpty(artist)) {

			return null;
		}

		return {
			artwork: artist.imagePath,
			...currentSong.serialized
		};
	}

	set currentSong (song) {
		this._currentSong = song;
	}

	@computed
	get songs () {
		return this._items;
	}

	set songs (songs) {
		this._items = songs;
	}

	@computed
	get singlesList () {
		return this.songs
			.filter(({ albumID }) => albumID === 'singles');
	}

	@computed
	get fullPreviewList () {
		return _(this.songs)
			.filter(({ isFullPreview }) => isFullPreview)
			.sortBy('fullPreviewPosition')
			.value();
	}

	@computed
	get tracksList () {
		return _(this.songs)
			.filter(({ isFullPreview }) => isFullPreview !== true)
			.sortBy('createdAt')
			.value();
	}

	@computed
	get newSongs () {
		return this.songs
			.filter(({ isNew }) => isNew);
	}


	@observable
	_songModalOpen = false;

	@computed
	get songModalOpen () {
		// If this.songToEdit has a value it will force this to return true
		return this._songModalOpen || _.isEmpty(this.songToEdit) !== true;
	}

	set songModalOpen (val) {
		this._songModalOpen = val;
	}

	@observable
	_songToEdit = false;

	@computed
	get songToEdit () {
		return this._songToEdit;
	}

	set songToEdit (val) {
		this._songToEdit = val;
	}

	@computed
	get activeSongRowID () {
		return this._activeSongRowID;
	}

	set activeSongRowID (id) {
		this._activeSongRowID = id;
	}

	@observable
	_searchTerm = '';

	@computed
	get searchTerm () {
		return this._searchTerm;
	}

	set searchTerm (searchTerm) {
		this._searchTerm = searchTerm;
	}

	@observable
	_searchResults = [];

	@computed
	get searchResults () {
		return this._searchResults;
	}

	set searchResults (searchResults) {
		this._searchResults = searchResults;
	}

	/**
	 * Actions
	 */

	@action
	setCurrentSong (currentSong) {
		this.currentSong = currentSong;
		playlistStore.currentPlaylist = null;
	}

	@action.bound
	onFetchSuccess (songs) {
		if (_.isEmpty(songs)){

			return;
		}

		this.songs = songs.map(this.mapUntitledSongs);
		this.setIsLoading(false);
		
		// Check if there are any songs in progress and start polling if needed
		this.checkAndStartPolling();
	}

	@action
	async updateFullPreviewPosition (song, position) {
		const opts = {
			method: 'put',
			route: routes.songFullPreviewPosition.replace(/\:id/, id)
		};

		const data = await this.save({ position, artistID }, opts);

		runInAction('Update after updating full prevoew position', () => {
			_.compact(data).forEach((updated) => {
				if (updated === null) {

					return;
				}

				const song = this.getSongByID(updated.id);
				song.updatedAt = updated.updatedAt;
				song.isFullPreview = updated.isFullPreview;
				song.fullPreviewPosition = updated.fullPreviewPosition;
			});
		});
	}

	@action
	async removeFullPreview ({ id }) {
		const route = routes.songFullPreviewRemove.replace(/\:id/, id);
		const method = 'put';

		return await this.save({}, { route, method });
	}

	@action
	newSong (data = {}) {
		const song = new Song(this, data);
		this.songs = [song].concat(this.songs);

		return song;
	}

	@action
	async saveSong (song, opts = {}) {
		const { id } = song;

		const options = {
			...opts,
			method: id ? 'put' : 'post',
			route: config.getRoute('song').replace(/\/\:id/, id ? `/${id}` : '')
		};

		try {

			const response = await this.save(song, options);
			// API attaches `linkedArtistSearchStatus` on song create (the
			// Atlas trigger updates the owning artist's searchStatus async;
			// this is the inline-refresh result so the artist CMS UI flips
			// without a refetch).
			if (response?.linkedArtistSearchStatus) {
				artistStore.applyLinkedArtistSearchStatus(response.linkedArtistSearchStatus);
			}
			return response;
		} catch (err) {

			console.error('[ERROR SongsStore.saveSong]', err);
		}
	}

	/**
	 * Override BaseStore.delete to apply `linkedArtistSearchStatus` from the
	 * response — deleting the last song flips hasEnoughSongs back to false,
	 * and the artist trigger updates async; without this the CTA doesn't
	 * re-appear until reload.
	 */
	@action
	async delete (item, opts = {}) {
		const response = await super.delete(item, opts);
		if (response?.linkedArtistSearchStatus) {
			artistStore.applyLinkedArtistSearchStatus(response.linkedArtistSearchStatus);
		}
		return response;
	}

	@action
	async uploadSongs (songFiles = [], artist) {
		const { id: artistID } = artist;
		this.isUpLoading = true;

		try {
			// Calculate total size of all files
			const totalSize = songFiles.reduce((sum, song) => sum + song.size, 0);
			let totalUploaded = 0;

			// First calculate durations and create song records
			const songRecords = await Promise.all(songFiles.map(async (songFile) => {
				// Calculate audio duration from the WAV file
				const duration = await new Promise((resolve) => {
					const audio = new Audio();
					audio.addEventListener('loadedmetadata', () => {
						resolve(Math.ceil(audio.duration));
					});
					audio.src = URL.createObjectURL(songFile);
				});

				const song = this.newSong({
					artistID,
					duration,
					active: false,
					title: songFile.name.replace(/\.[^/.]+$/, ''), // Remove file extension
					status: 'uploading'
				});

				console.log('[SongsStore.uploadSongs] songData'. song);
				await song.save();
				return song;
			}));

			// Then get presigned URLs for each song
			const presignedData = await apiStore.save({
				route: routes.songUploadPresigned.replace(/\:id/, artistID),
				payload: {
					files: songFiles.map((song, index) => ({
						name: song.name,
						size: song.size,
						type: song.type,
						s3Path: `${artistID}/${songRecords[index].id}` // Use ARTIST_ID/SONG_ID structure
					}))
				}
			});

			// Initialize progress tracking
			runInAction(() => {
				apiStore._progressTotal = totalSize;
				apiStore._progressLoaded = 0;
			});

			// Upload each file using the presigned URLs
			const uploadPromises = songFiles.map(async (songFile, songIndex) => {
				const { uploadId, key, urls } = presignedData[songIndex];
				const songId = songRecords[songIndex].id;
				const chunkSize = 5 * 1024 * 1024; // 5MB chunks
				const chunks = Math.ceil(songFile.size / chunkSize);
				const parts = [];

				for (let i = 0; i < chunks; i++) {
					const start = i * chunkSize;
					const end = Math.min(start + chunkSize, songFile.size);
					const chunk = songFile.slice(start, end);

					const response = await apiStore.ax({
						method: 'PUT',
						url: urls[i],
						data: chunk,
						headers: {
							'Content-Type': songFile.type
						},
						transformRequest: [(data) => data],
						withCredentials: false,
						maxBodyLength: Infinity,
						maxContentLength: Infinity,
						onUploadProgress: (progressEvent) => {
							// Calculate progress for this chunk
							const chunkProgress = progressEvent.loaded;
							
							// Update total progress
							runInAction(() => {
								apiStore._progressLoaded = totalUploaded + chunkProgress;
							});
						}
					});

					const rawEtag = response.headers['etag'] || 
						response.headers['ETag'] || 
						response.headers['Etag'] || 
						response.headers['etaG'];

					if (!rawEtag) {
						console.error('Headers received:', response.headers);
						throw new Error(`No ETag received for part ${i + 1}`);
					}

					const etag = rawEtag.replace(/^['"]\s*|\s*['"]$/g, '').trim();

					if (!etag) {
						throw new Error(`Invalid ETag format for part ${i + 1}: ${rawEtag}`);
					}

					parts.push({
						ETag: etag,
						PartNumber: i + 1
					});

					// Update total uploaded after chunk is complete
					totalUploaded += chunk.size;
					runInAction(() => {
						apiStore._progressLoaded = totalUploaded;
					});
				}

				// Complete the multipart upload, update song status, and queue for processing
				await apiStore.save({
					route: routes.songUploadComplete.replace(/\:id/, songId),
					payload: {
						key,
						uploadId,
						parts,
						artistID,
						s3Path: `${artistID}/${songId}`,
						fileName: songFile.name
					}
				});

				return songRecords[songIndex];
			});

			const results = await Promise.all(uploadPromises);

			// Reset progress after completion
			runInAction(() => {
				apiStore._progressLoaded = 0;
				apiStore._progressTotal = 0;
			});

			// Start polling for all songs at once
			this.pollSongsStatus(artistID);

			return results;
		} catch (err) {
			console.error('[ERROR SongsStore.uploadSongs]', err);
			throw err;
		} finally {
			this.isUpLoading = false;
		}
	}

	@action
	async fetchSongsByArtist (artistID) {
		const route = routes.songsByArtist.replace(/:artistID/, artistID);

		this.setIsLoading(true);

		try {

			await this.fetch({ route });
			
			// After fetching songs, check if any are in progress and start polling
			this.checkAndStartPolling();
		} catch (error) {

			console.error('[ERROR SongsStore.fetchByArtist]', error);
		} finally {

			this.setIsLoading(false);
		}
	}

	@action.bound
	search () {
		if (_.isEmpty(this.searchTerm) || this.searchTerm.length < 3) {

			this.searchResults = [];
			return;
		}

		const re = new RegExp(this.searchTerm, 'i');
		this.searchResults = this.songs.filter((song) => {
			const artist = artistStore.getArtistByID(song.artistID);

			return re.test(song.title) && artist && artist.isSearchable;
		});
	}

	@action
	async checkAndStartPolling () {
		// Don't start polling if already polling
		if (this._isPolling) {
			return;
		}

		// Check if there are any songs that are not in 'ready' status
		const songsInProgress = this.songs.filter(song => song.status !== 'ready');
		
		if (songsInProgress.length > 0) {
			// Get the artist ID from the first song (assuming all songs belong to the same artist)
			const artistID = songsInProgress[0].artistID;
			console.log(`[SongsStore.checkAndStartPolling] Found ${songsInProgress.length} songs in progress for artist ${artistID}`);
			this.pollSongsStatus(artistID);
		}
	}

	@action
	async pollSongsStatus (artistID) {
		// Prevent duplicate polling
		if (this._isPolling) {
			console.log(`[SongsStore.pollSongsStatus] Already polling for artist ${artistID}`);
			return;
		}

		this._isPolling = true;
		const pollInterval = 2000; // Poll every 2 seconds
		const maxAttempts = 60; // Maximum polling attempts (2 minutes)
		let attempts = 0;
		
		const poll = async () => {
			if (attempts >= maxAttempts) {
				console.log(`[SongsStore.pollSongsStatus] Max attempts reached for artist ${artistID}, refreshing song list`);
				this._isPolling = false;
				await this.fetchSongsByArtist(artistID);
				return;
			}
			
			try {
				const route = routes.songsStatus.replace(/\:artistID/, artistID);
				const { songs } = await apiStore.fetch({ route });

				if (songs && songs.length > 0) {
					// Update in-progress song statuses from the response
					runInAction(() => {
						songs.forEach(songStatus => {
							const song = this.getSongByID(songStatus.id);

							if (song) {
								song.update({
									status: songStatus.status,
									genre: songStatus.genre,
									mood: songStatus.mood
								});
							}
						});
					});
				} else {
					// Empty response means all songs are now ready — refresh the full
					// list so the store reflects the current status from the server.
					console.log(`[SongsStore.pollSongsStatus] All songs ready for artist ${artistID}, refreshing song list`);
					this._isPolling = false;
					await this.fetchSongsByArtist(artistID);
					return;
				}
				
				// Continue polling
				attempts++;
				setTimeout(poll, pollInterval);
			} catch (err) {
				console.error(`[ERROR SongsStore.pollSongsStatus] ${err}`);
				attempts++;
				setTimeout(poll, pollInterval);
			}
		};
		
		// Start polling
		poll();
	}

	/**
	 * Methods
	 */

	getSongIndex (song) {
		return this.getItemIndex(song);
	}

	getSongsByArtistID (id) {
		return this.songs
			.filter(({ artistID }) => artistID === id);
	}

	getSongByID (value) {
		if (_.isObject(value) && _.isFunction(value.slice)) {

			return _(value.slice())
				.map((id) => this.getItemByID(id))
				.compact()
				.value();
		}

		const id = _.isObject(value) ? value.id : value;
		return this.getItemByID(id);
	}

	@autobind
	mapUntitledSongs (item) {
		if (_.isEmpty(item.title)) {

			item.title = 'Untitled';
		}

		return new Song(this, item);
	}
}

const songsStore = window.songsStore = new SongsStore();
export default songsStore;
